import { Button } from '@shelflix/ui-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shelflix/ui-web/components/ui/dialog';

interface UserDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  canDelete: boolean;
  userName?: string;
  dependencies?: {
    hasSubordinates: boolean;
    subordinates: Array<{ uuid: string; name: string }>;
    hasFacilities: boolean;
    facilities: Array<{ uuid: string; name: string }>;
  };
  isLoading?: boolean;
}

export function UserDeletionModal({
  isOpen,
  onClose,
  onConfirm,
  canDelete,
  userName,
  dependencies,
  isLoading = false,
}: UserDeletionModalProps) {
  // Render warning modal (cannot delete due to dependencies)
  const renderWarningModal = () => {
    if (!dependencies) return null;

    const subordinates = dependencies.subordinates || [];
    const facilities = dependencies.facilities || [];

    return (
      <Dialog key="warning-modal" open={isOpen} onOpenChange={onClose}>
        <DialogContent className="flex max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:max-h-[85vh] sm:max-w-lg">
          <div className="flex-shrink-0 p-6 pb-4">
            <DialogHeader className="text-left">
              <DialogTitle className="text-[var(--primary)]">Não é possível excluir o usuário</DialogTitle>
              <DialogDescription>
                Este usuário possui vínculos no sistema que impedem sua exclusão. Resolva os vínculos abaixo antes de
                prosseguir.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-4">
            {subordinates.length > 0 && (
              <div className="mb-4">
                <h4 className="mb-2 text-sm font-semibold text-[var(--primary)]">
                  Subordinados ({subordinates.length}):
                </h4>
                <p className="text-muted-foreground mb-2 text-xs">
                  Reatribua estes usuários para outro responsável antes de excluir.
                </p>
                <ul className="space-y-2">
                  {subordinates.map((subordinate) => (
                    <li key={subordinate.uuid} className="flex items-center gap-2 pl-4 text-sm">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--primary)]" />
                      <span className="font-medium break-words">{subordinate.name}</span>
                    </li>
                  ))}
                  {subordinates.length >= 10 && (
                    <li className="text-muted-foreground pl-4 text-sm italic">... e possivelmente mais usuários</li>
                  )}
                </ul>
              </div>
            )}

            {facilities.length > 0 && (
              <div className="mb-4">
                <h4 className="mb-2 text-sm font-semibold text-[var(--primary)]">
                  Estabelecimentos ({facilities.length}):
                </h4>
                <p className="text-muted-foreground mb-2 text-xs">
                  Este usuário é responsável por estes estabelecimentos. Altere o responsável antes de excluir.
                </p>
                <ul className="space-y-2">
                  {facilities.map((facility) => (
                    <li key={facility.uuid} className="flex items-center gap-2 pl-4 text-sm">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--primary)]" />
                      <span className="font-medium break-words">{facility.name}</span>
                    </li>
                  ))}
                  {facilities.length >= 10 && (
                    <li className="text-muted-foreground pl-4 text-sm italic">
                      ... e possivelmente mais estabelecimentos
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <div className="flex-shrink-0 p-6 pt-0">
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Render confirmation modal (can delete)
  const renderConfirmationModal = () => {
    return (
      <Dialog key="confirmation-modal" open={isOpen} onOpenChange={onClose}>
        <DialogContent className="flex max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:max-h-[85vh] sm:max-w-lg">
          <div className="flex-shrink-0 p-6 pb-4">
            <DialogHeader>
              <DialogTitle>Confirmar exclusão de usuário</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir o usuário <strong>{userName}</strong>? Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-shrink-0 p-6 pt-0">
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={onConfirm} loading={isLoading}>
                Excluir usuário
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Only render the appropriate modal based on canDelete state
  if (!canDelete && dependencies) {
    return renderWarningModal();
  }

  if (canDelete) {
    return renderConfirmationModal();
  }

  return null;
}
